import { ResponsiveTable } from '../components/ResponsiveTable';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  Edit,
  ExternalLink,
  Filter,
  Loader2,
  Plus,
  Search,
  Trash2,
  XCircle,
} from 'lucide-react';
import { RootState } from '../store';
import { ConfirmDefinitiveDeleteModal } from '../components/ConfirmDefinitiveDeleteModal';
import { HelpIcon } from '../components/HelpIcon';
import { TableScrollContainer } from '../components/TableScrollContainer';
import { useToast } from '../components/Toast';
import { useVisibleSelection } from '../hooks/useVisibleSelection';
import {
  deleteTask,
  getTasks,
  hardDeleteTask,
  Task,
  TASK_STATUS_BADGE,
  TaskStatus,
  TaskType,
} from '../services';

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const SORT_FIELDS = [
  'title',
  'taskType',
  'status',
  'assignedTo',
  'startDate',
  'endDate',
  'effectiveEndDate',
  'createdAt',
] as const;
type SortField = (typeof SORT_FIELDS)[number];

function getFieldValue(task: Task, field: SortField): string | number {
  switch (field) {
    case 'title':
      return task.title.toLowerCase();
    case 'taskType':
      return task.taskType;
    case 'status':
      return task.status;
    case 'assignedTo':
      return (task.assignedTo?.email ?? task.assignedToId).toLowerCase();
    case 'startDate':
      return task.startDate ? new Date(task.startDate).getTime() : 0;
    case 'endDate':
      return task.endDate ? new Date(task.endDate).getTime() : Infinity;
    case 'effectiveEndDate':
      return task.effectiveEndDate ? new Date(task.effectiveEndDate).getTime() : Infinity;
    case 'createdAt':
      return new Date(task.createdAt).getTime();
    default:
      return '';
  }
}

const StatusIcon: Record<TaskStatus, React.ReactNode> = {
  PENDING: <Clock className="w-3.5 h-3.5" />,
  IN_PROGRESS: <Loader2 className="w-3.5 h-3.5 animate-spin" />,
  DONE: <CheckCircle2 className="w-3.5 h-3.5" />,
  CANCELLED: <XCircle className="w-3.5 h-3.5" />,
};

// ── Component ─────────────────────────────────────────────────────────────────

export function TasksListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast, ToastComponent } = useToast();
  const { user } = useSelector((state: RootState) => state.auth);

  // Role-based delete permission: super_admin, admin, company_admin, shop_admin, manager
  const role = String((user as any)?.role || '').toLowerCase().replace(/-/g, '_');
  const canDelete =
    role === 'super_admin' ||
    role === 'admin' ||
    role === 'company_admin' ||
    role === 'shop_admin' ||
    role === 'manager';
  const canHardDelete = role === 'super_admin';

  // ── Data state ─────────────────────────────────────────────────────────────
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Filters ────────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<TaskStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<TaskType | ''>('');

  // ── Sorting ────────────────────────────────────────────────────────────────
  const [sortKey, setSortKey] = useState<SortField>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // ── Pagination ─────────────────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  // ── Selection ──────────────────────────────────────────────────────────────
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    id: string;
    title: string;
  }>({ isOpen: false, id: '', title: '' });
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState<{
    isOpen: boolean;
    ids: string[];
  }>({ isOpen: false, ids: [] });
  const [bulkHardDeleteConfirm, setBulkHardDeleteConfirm] = useState<{
    isOpen: boolean;
    ids: string[];
  }>({ isOpen: false, ids: [] });
  const [isDeleting, setIsDeleting] = useState(false);
  const [isHardDeleting, setIsHardDeleting] = useState(false);
  const isBusy = loading || isDeleting || isHardDeleting;

  // ── Load ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    void loadTasks();
  }, [statusFilter, typeFilter]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getTasks({
        status: statusFilter || undefined,
        taskType: typeFilter || undefined,
      });
      setTasks(data);
      setPage(1);
    } catch (err: any) {
      const msg = err.message || t('tasks.toast.loadError');
      setError(msg);
      showToast(msg, 'error', 7000);
    } finally {
      setLoading(false);
    }
  };

  // ── Client-side filter + sort ──────────────────────────────────────────────
  const filteredSorted = useMemo(() => {
    const term = search.trim().toLowerCase();
    let result = tasks.filter((t) => {
      if (!term) return true;
      return [t.title, t.description ?? '', t.assignedTo?.email ?? ''].join(' ').toLowerCase().includes(term);
    });

    result = [...result].sort((a, b) => {
      const av = getFieldValue(a, sortKey);
      const bv = getFieldValue(b, sortKey);
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [tasks, search, sortKey, sortDir]);

  // ── Pagination slice ───────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(filteredSorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginated = filteredSorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // ── Selection (scoped to paginated ids) ───────────────────────────────────
  const visibleIds = useMemo(() => paginated.map((t) => t.id), [paginated]);
  const {
    selectedIds,
    selectedCount,
    clearSelection,
    selectAllCheckboxRef,
    allVisibleSelected,
    toggleSelect,
    toggleSelectAllVisible,
  } = useVisibleSelection<string>(visibleIds);

  // ── Sort handler ──────────────────────────────────────────────────────────
  const handleSort = (key: SortField) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortKey !== field) return <ChevronDown className="w-3.5 h-3.5 text-gray-400 opacity-50" />;
    return sortDir === 'asc' ? (
      <ChevronUp className="w-3.5 h-3.5 text-primary-600" />
    ) : (
      <ChevronDown className="w-3.5 h-3.5 text-primary-600" />
    );
  };

  // ── Single delete ─────────────────────────────────────────────────────────
  const handleDeleteClick = (task: Task) => {
    setDeleteConfirm({ isOpen: true, id: task.id, title: task.title });
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteTask(deleteConfirm.id);
      showToast(t('tasks.toast.deleted'), 'success');
      setDeleteConfirm({ isOpen: false, id: '', title: '' });
      await loadTasks();
    } catch (err: any) {
      showToast(err.message || t('tasks.toast.deleteError'), 'error', 7000);
    } finally {
      setIsDeleting(false);
    }
  };

  // ── Bulk delete ───────────────────────────────────────────────────────────
  const openBulkDeleteConfirm = () => {
    if (selectedCount === 0) return;
    setBulkDeleteConfirm({ isOpen: true, ids: Array.from(selectedIds) });
  };

  const confirmBulkDelete = async () => {
    if (bulkDeleteConfirm.ids.length === 0) return;
    setIsDeleting(true);
    const failed: string[] = [];
    try {
      for (const id of bulkDeleteConfirm.ids) {
        try {
          await deleteTask(id);
        } catch {
          failed.push(id);
        }
      }
      const deleted = bulkDeleteConfirm.ids.length - failed.length;
      setBulkDeleteConfirm({ isOpen: false, ids: [] });
      clearSelection();
      await loadTasks();

      if (failed.length === 0) {
        showToast(t('tasks.toast.bulkDeleted_other', { count: deleted }), 'success');
      } else if (deleted === 0) {
        showToast(t('tasks.toast.bulkFailed', { failed: failed.length }), 'error', 7000);
      } else {
        showToast(
          t('tasks.toast.bulkPartial', { deleted, failed: failed.length }),
          'error',
          7000,
        );
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const openBulkHardDeleteConfirm = () => {
    if (!canHardDelete || selectedCount === 0) return;
    setBulkHardDeleteConfirm({ isOpen: true, ids: Array.from(selectedIds) });
  };

  const confirmBulkHardDelete = async () => {
    if (bulkHardDeleteConfirm.ids.length === 0) return;
    setIsHardDeleting(true);
    const failed: string[] = [];
    try {
      setError(null);
      for (const id of bulkHardDeleteConfirm.ids) {
        try {
          await hardDeleteTask(id);
        } catch {
          failed.push(id);
        }
      }

      const deleted = bulkHardDeleteConfirm.ids.length - failed.length;
      setBulkHardDeleteConfirm({ isOpen: false, ids: [] });
      clearSelection();
      await loadTasks();

      if (failed.length === 0) {
        showToast(t('tasks.toast.bulkHardDeleted_other', { count: deleted }), 'success');
      } else if (deleted === 0) {
        const msg = t('tasks.toast.bulkHardFailed', { failed: failed.length });
        setError(msg);
        showToast(msg, 'error', 7000);
      } else {
        const msg = t('tasks.toast.bulkHardPartial', { deleted, failed: failed.length });
        setError(msg);
        showToast(msg, 'error', 7000);
      }
    } finally {
      setIsHardDeleting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className={`p-6 ${isBusy ? 'cursor-wait' : ''}`}>
      <ToastComponent />

      {/* Header */}
      <div className="mb-6 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center gap-2">
              {t('tasks.title')}
              <HelpIcon docPath="17-gestion-taches#17-2-liste" tooltip="Documentation : Gestion des tâches" />
            </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {t('tasks.total_other', { count: filteredSorted.length })}
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {selectedCount > 0 && canDelete && (
            <>
              <button
                onClick={openBulkDeleteConfirm}
                disabled={isDeleting || isHardDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2 text-sm font-medium"
              >
                <Trash2 className="w-4 h-4" />
                {t('tasks.bulk.delete', { count: selectedCount })}
              </button>
              {canHardDelete && (
                <button
                  onClick={openBulkHardDeleteConfirm}
                  disabled={isDeleting || isHardDeleting}
                  className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50 inline-flex items-center gap-2 text-sm font-medium"
                >
                  <AlertCircle className="w-4 h-4" />
                  {t('tasks.bulk.hardDelete', { count: selectedCount })}
                </button>
              )}
            </>
          )}
          {selectedCount > 0 && (
            <span className="text-sm text-gray-600">
              {t('tasks.bulk.selected_other', { count: selectedCount })}
            </span>
          )}
          <button
            onClick={() => navigate('/tasks/create')}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            {t('tasks.newTask')}
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => void loadTasks()} className="text-sm underline">
            {t('tasks.retry')}
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <div className="dt-filters grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          {/* Search */}
          <div className="relative md:col-span-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder={t('tasks.filters.search')}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-sm"
            />
          </div>

          {/* Status filter */}
          <div className="relative md:col-span-3">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value as TaskStatus | ''); setPage(1); }}
              className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 appearance-none text-sm"
            >
              <option value="">{t('tasks.filters.allStatuses')}</option>
              {(['PENDING', 'IN_PROGRESS', 'DONE', 'CANCELLED'] as TaskStatus[]).map((s) => (
                <option key={s} value={s}>{t(`tasks.status.${s}`)}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>

          {/* Type filter */}
          <div className="relative md:col-span-3">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <select
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value as TaskType | ''); setPage(1); }}
              className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 appearance-none text-sm"
            >
              <option value="">{t('tasks.filters.allTypes')}</option>
              {(
                [
                  'PIERCING_ASSIGNMENT',
                  'DOCUMENT_REVIEW',
                  'CONSENT_FORM',
                  'DEPOSIT_FOLLOW_UP',
                  'APPOINTMENT_PREPARATION',
                  'PRACTITIONER_ONBOARDING',
                  'CUSTOM',
                ] as TaskType[]
              ).map((tp) => (
                <option key={tp} value={tp}>{t(`tasks.type.${tp}`)}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>

          {/* Clear */}
          <button
            onClick={() => { setSearch(''); setStatusFilter(''); setTypeFilter(''); setPage(1); }}
            className="btn-secondary flex items-center justify-center gap-2 md:col-span-2 text-sm"
          >
            <Filter className="w-4 h-4" />
            <span>{t('tasks.filters.clear')}</span>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <TableScrollContainer hint={t('tasks.scrollHint')}>
          <ResponsiveTable className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {/* Checkbox */}
                <th className="px-4 py-3 w-10">
                  <input
                    ref={selectAllCheckboxRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    disabled={paginated.length === 0 || isDeleting || isHardDeleting}
                    onChange={toggleSelectAllVisible}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    aria-label={t('tasks.bulk.selectAll')}
                  />
                </th>

                <SortableTh field="title" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} SortIcon={SortIcon}>
                  {t('tasks.columns.title')}
                </SortableTh>
                <SortableTh field="taskType" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} SortIcon={SortIcon}>
                  {t('tasks.columns.type')}
                </SortableTh>
                <SortableTh field="status" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} SortIcon={SortIcon}>
                  {t('tasks.columns.status')}
                </SortableTh>
                <SortableTh field="assignedTo" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} SortIcon={SortIcon}>
                  {t('tasks.columns.assignedTo')}
                </SortableTh>
                <SortableTh field="startDate" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} SortIcon={SortIcon}>
                  {t('tasks.columns.startDate')}
                </SortableTh>
                <SortableTh field="endDate" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} SortIcon={SortIcon}>
                  {t('tasks.columns.endDate')}
                </SortableTh>
                <SortableTh field="effectiveEndDate" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} SortIcon={SortIcon}>
                  {t('tasks.columns.effectiveEndDate')}
                </SortableTh>
                <SortableTh field="createdAt" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} SortIcon={SortIcon}>
                  {t('tasks.columns.createdAt')}
                </SortableTh>

                {/* Link (not sortable) */}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('tasks.columns.link')}
                </th>

                {/* Actions – sticky right */}
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-10 border-l border-gray-200">
                  {t('tasks.columns.actions')}
                </th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={11} className="px-4 py-10 text-center text-gray-500">
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
                      {t('tasks.loading')}
                    </div>
                  </td>
                </tr>
              ) : paginated.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-14 text-center text-gray-500">
                    <p className="font-medium">{t('tasks.noTasksFound')}</p>
                    <p className="text-sm mt-1">
                      {search || statusFilter || typeFilter
                        ? t('tasks.noTasksHint')
                        : t('tasks.noTasksEmpty')}
                    </p>
                  </td>
                </tr>
              ) : (
                paginated.map((task) => {
                  const isOverdue =
                    task.endDate &&
                    new Date(task.endDate) < new Date() &&
                    task.status !== 'DONE' &&
                    task.status !== 'CANCELLED';

                  return (
                    <tr
                      key={task.id}
                      className={`group hover:bg-gray-50 ${selectedIds.has(task.id) ? 'bg-primary-50' : ''}`}
                    >
                      {/* Checkbox */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(task.id)}
                          disabled={isDeleting || isHardDeleting}
                          onChange={() => toggleSelect(task.id)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          aria-label={t('tasks.bulk.selectRow', { title: task.title })}
                        />
                      </td>

                      {/* Title */}
                      <td className="px-4 py-3 min-w-[200px] max-w-[280px]">
                        <div className="text-sm font-medium text-gray-900 truncate">{task.title}</div>
                        {task.description && (
                          <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">{task.description}</div>
                        )}
                      </td>

                      {/* Type */}
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {t(`tasks.type.${task.taskType}`)}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${TASK_STATUS_BADGE[task.status]}`}
                        >
                          {StatusIcon[task.status]}
                          {t(`tasks.status.${task.status}`)}
                        </span>
                      </td>

                      {/* Assigned to */}
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {task.assignedTo?.email ?? task.assignedToId}
                      </td>

                      {/* Start date */}
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(task.startDate)}
                      </td>

                      {/* Deadline */}
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <span className={isOverdue ? 'text-red-600 font-semibold' : 'text-gray-600'}>
                          {formatDate(task.endDate)}
                        </span>
                      </td>

                      {/* Effective end */}
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(task.effectiveEndDate)}
                      </td>

                      {/* Created at */}
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(task.createdAt)}
                      </td>

                      {/* Link */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        {task.url ? (
                          <button
                            onClick={() => navigate(task.url!)}
                            className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 hover:underline"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                            {t('tasks.actions.open')}
                          </button>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>

                      {/* Actions – sticky */}
                      <td className="px-4 py-3 whitespace-nowrap text-right sticky right-0 bg-white group-hover:bg-gray-50 z-10 border-l border-gray-200">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => navigate(`/tasks/${task.id}/edit`)}
                            disabled={isDeleting || isHardDeleting}
                            className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                            title={t('tasks.actions.edit')}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          {canDelete && (
                            <button
                              onClick={() => handleDeleteClick(task)}
                              disabled={isDeleting || isHardDeleting}
                              className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
                              title={t('tasks.actions.delete')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </ResponsiveTable>
        </TableScrollContainer>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="dt-list-toolbar bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
              <p className="text-sm text-gray-700">
                {t('common.showing', {
                  from: (safePage - 1) * PAGE_SIZE + 1,
                  to: Math.min(safePage * PAGE_SIZE, filteredSorted.length),
                  total: filteredSorted.length,
                }) ||
                  `Affichage de ${(safePage - 1) * PAGE_SIZE + 1} à ${Math.min(safePage * PAGE_SIZE, filteredSorted.length)} sur ${filteredSorted.length}`}
              </p>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => setPage(Math.max(1, safePage - 1))}
                  disabled={safePage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const n = Math.max(1, Math.min(totalPages - 4, safePage - 2)) + i;
                  return (
                    <button
                      key={n}
                      onClick={() => setPage(n)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        safePage === n
                          ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {n}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPage(Math.min(totalPages, safePage + 1))}
                  disabled={safePage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </nav>
            </div>
            {/* Mobile */}
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPage(Math.max(1, safePage - 1))}
                disabled={safePage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Précédent
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, safePage + 1))}
                disabled={safePage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Loading overlay for refetch */}
      {loading && tasks.length > 0 && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50 cursor-wait">
          <div className="bg-white rounded-lg p-4 flex items-center gap-2 shadow-lg">
            <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
            <span>{t('tasks.loading')}</span>
          </div>
        </div>
      )}

      {/* Single delete confirm */}
      {deleteConfirm.isOpen && (
        <div
          className={`fixed inset-0 bg-black/50 flex items-center justify-center z-50 ${isDeleting ? 'cursor-wait' : ''}`}
        >
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold mb-3">{t('tasks.confirm.deleteTitle')}</h3>
            <p className="text-gray-600 mb-6 text-sm">
              {t('tasks.confirm.deleteMessage', { title: deleteConfirm.title })}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm({ isOpen: false, id: '', title: '' })}
                disabled={isDeleting}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm"
              >
                {t('tasks.confirm.cancel')}
              </button>
              <button
                onClick={() => void handleConfirmDelete()}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm inline-flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('tasks.confirm.deleting')}
                  </>
                ) : (
                  t('tasks.confirm.confirm')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk delete confirm */}
      {bulkDeleteConfirm.isOpen && (
        <div
          className={`fixed inset-0 bg-black/50 flex items-center justify-center z-50 ${isDeleting ? 'cursor-wait' : ''}`}
        >
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold mb-3">{t('tasks.confirm.deleteTitle')}</h3>
            <p className="text-gray-600 mb-6 text-sm">
              {t('tasks.confirm.bulkDeleteMessage', { count: bulkDeleteConfirm.ids.length })}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setBulkDeleteConfirm({ isOpen: false, ids: [] })}
                disabled={isDeleting}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm"
              >
                {t('tasks.confirm.cancel')}
              </button>
              <button
                onClick={() => void confirmBulkDelete()}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm inline-flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('tasks.confirm.deleting')}
                  </>
                ) : (
                  t('tasks.confirm.confirm')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDefinitiveDeleteModal
        isOpen={bulkHardDeleteConfirm.isOpen}
        onConfirm={confirmBulkHardDelete}
        onClose={() => setBulkHardDeleteConfirm({ isOpen: false, ids: [] })}
        entityLabel="tâche(s)"
        count={bulkHardDeleteConfirm.ids.length}
        isLoading={isHardDeleting}
      />
    </div>
  );
}

// ── SortableTh ────────────────────────────────────────────────────────────────

interface SortableThProps {
  field: SortField;
  sortKey: SortField;
  sortDir: 'asc' | 'desc';
  onSort: (f: SortField) => void;
  SortIcon: React.ComponentType<{ field: SortField }>;
  children: React.ReactNode;
}

function SortableTh({ field, onSort, SortIcon, children }: SortableThProps) {
  return (
    <th
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none whitespace-nowrap"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        <span>{children}</span>
        <SortIcon field={field} />
      </div>
    </th>
  );
}
